import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Blog APIs
export const blogAPI = {
  getAll: (params) => api.get('/blogs', { params }),
  getFeatured: () => api.get('/blogs/featured'),
  getCategories: () => api.get('/blogs/categories'),
  getBySlug: (slug) => api.get(`/blogs/${slug}`),
  create: (data) => api.post('/blogs', data),
  update: (id, data) => api.put(`/blogs/${id}`, data),
  delete: (id) => api.delete(`/blogs/${id}`),
};

// Service APIs
export const serviceAPI = {
  getAll: () => api.get('/services'),
  getFeatured: () => api.get('/services/featured'),
  getCategories: () => api.get('/services/categories'),
  getBySlug: (slug) => api.get(`/services/${slug}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Lead APIs
export const leadAPI = {
  create: (data) => api.post('/leads', data),
  createQuote: (data) => api.post('/leads/quote', data),
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  addNote: (id, data) => api.post(`/leads/${id}/notes`, data),
  delete: (id) => api.delete(`/leads/${id}`),
};

// Project APIs
export const projectAPI = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  addMilestone: (id, data) => api.post(`/projects/${id}/milestones`, data),
  updateMilestone: (id, milestoneId, data) => api.put(`/projects/${id}/milestones/${milestoneId}`, data),
  addUpdate: (id, data) => api.post(`/projects/${id}/updates`, data),
  addDocument: (id, data) => api.post(`/projects/${id}/documents`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Ticket APIs
export const ticketAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  addResponse: (id, data) => api.post(`/tickets/${id}/responses`, data),
  assign: (id, adminId) => api.put(`/tickets/${id}/assign`, { adminId }),
  resolve: (id, resolution) => api.put(`/tickets/${id}/resolve`, { resolution }),
  submitSatisfaction: (id, data) => api.post(`/tickets/${id}/satisfaction`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
};

// Chatbot APIs
export const chatbotAPI = {
  createSession: (data) => api.post('/chatbot/session', data),
  sendMessage: (data) => api.post('/chatbot/message', data),
  convertToLead: (data) => api.post('/chatbot/lead', data),
  getSessions: () => api.get('/chatbot/sessions'),
};

// Admin APIs
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
};

export default api;
